import { Badge } from "@/app/components/shadcn/badge";
import { Button } from "@/app/components/shadcn/button";
import { Skeleton } from "@/app/components/shadcn/skeleton";
import { ChevronRight, Check, Info, Store } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useStore from "@/app/lib/features/store/store.hook";
import useHasAuthToken from "@/app/hooks/useHasAuthToken";
import { toggleStoreFollow } from "@/app/lib/features/store/store.thunk";
import useModal from "@/app/lib/features/modal/modal.hook";
import { trackSellerFollow } from "@/app/lib/tracking/interactions";

const SellerInfo = () => {
  const { product, isAuthDataSettled } = useProductDetail();
  const { toggleFollow, isFollowLoading } = useStore();
  const hasAuthToken = useHasAuthToken();
  const { createModal } = useModal();
  const [failedBadges, setFailedBadges] = useState<Set<string>>(new Set());

  // `is_following` auth'a bağlı ama SSR/ISR HTML'i anonim üretiliyor — auth'lu
  // client fetch dönene kadar takipçide "Takip Et" → "Takip Ediliyor" sıçraması
  // oluyordu. Misafirde değer kesin false olduğundan bekleme yok.
  const isFollowStateKnown = !hasAuthToken || isAuthDataSettled;

  if (!product || !product.seller) return null;

  const { seller, shipping } = product;

  const handleFollowClick = async () => {
    const result = await toggleFollow(seller.id);

    if (toggleStoreFollow.fulfilled.match(result)) {
      if (result.payload.is_following) {
        trackSellerFollow(seller.id);
        createModal("follow-reward", { followData: result.payload });
      }
    }
  };

  return (
    <div className="bg-white border p-2 rounded-md space-y-2 relative mb-8">
      <div className="bg-[#ebf5ff] p-2 rounded-md">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            href={`/magaza/${seller.slug}`}
            className="text-[#1f6bc1] text-[13px] font-medium"
          >
            {seller.name}
          </Link>
          {seller.rating && seller.rating > 0 && (
            <Badge size="sm">{seller.rating.toFixed(1)}</Badge>
          )}
          {(seller.info || seller.badges.length > 0) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="flex items-center">
                  <Info className="text-[#1f6bc1]" size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                variant="white"
                side="top"
                className="max-w-xs p-3 space-y-3"
              >
                {seller.info && (
                  <p className="text-xs text-gray-600">{seller.info}</p>
                )}
                {seller.badges.length > 0 && (
                  <div
                    className={`space-y-2 ${seller.info ? "border-t border-gray-100 pt-3" : ""}`}
                  >
                    {seller.badges.map((badge) => (
                      <div key={badge.key} className="flex items-start gap-2">
                        {!failedBadges.has(badge.key) && (
                          <Image
                            src={badge.icon}
                            alt={badge.name}
                            width={18}
                            height={18}
                            className="shrink-0 mt-0.5"
                            onError={() =>
                              setFailedBadges((prev) =>
                                new Set(prev).add(badge.key),
                              )
                            }
                          />
                        )}
                        <div>
                          <p className="text-xs font-semibold text-gray-900">
                            {badge.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {badge.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className="text-[10px] text-[#666] font-medium mt-1 mb-2">
          {seller.follower_count.toLocaleString("tr-TR")} Takipçi
        </p>
        {shipping?.is_fast && (
          <Image
            src="/images/icons/hizli-satici.svg"
            alt="Hizli Satici"
            width={71}
            height={18}
          />
        )}
      </div>

      {isFollowStateKnown ? (
        <button
          type="button"
          onClick={handleFollowClick}
          disabled={isFollowLoading}
          className="cursor-pointer flex items-center gap-2 w-full disabled:opacity-60"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-[#f5f5f5] rounded-md shrink-0">
            {seller.is_following ? (
              <Check size={20} className="text-main" />
            ) : (
              <Store size={20} />
            )}
          </div>
          <p className="text-xs font-medium">
            {seller.is_following ? "Takip Ediliyor" : "Takip Et"}
          </p>
        </button>
      ) : (
        <div className="flex items-center gap-2 w-full">
          <Skeleton className="w-8 h-8 rounded-md shrink-0" />
          <Skeleton className="h-3 w-24 rounded-md" />
        </div>
      )}

      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
        <Link href={`/magaza/${seller.slug}`}>
          <Button
            variant="outline"
            className="rounded-full px-4 sm:px-6 whitespace-nowrap"
          >
            Magazaya Git
            <ChevronRight size={16} className="sm:w-4.5 sm:h-4.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default SellerInfo;
